import React from 'react'
import { X, Mail } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface ChangePasswordModalProps {
  email: string
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  isSubmitting?: boolean
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  email,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  if (!isOpen) return null

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
            <p className="mt-1 text-sm text-gray-600">
              Passwords are managed securely through Auth0
            </p>
          </div>

          {/* Info */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg mb-6">
            <Mail className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600">
              We'll send a password reset link to <span className="font-medium text-gray-900">{email}</span>.
              Follow the link to choose a new password.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onConfirm}
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Send Reset Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangePasswordModal
